<template>
  <div>
    <div class="mx-0 md:mx-12 lg:mx-0">
      <div v-if="item.Message !== ''">
        <div
          class="text-sm mt-4 mb-2 html-content"
          v-html="parsedMessage"
        ></div>
      </div>

      <button
        v-if="isButtonClickable && item.Promolinktext.length"
        class="button button-primary mt-2"
        :aria-label="
          $t('Components.Timeline.TimelinePromotedItem.promotedMessage')
        "
        @click="onButtonClick"
      >
        {{ item.Promolinktext }}
      </button>
    </div>
  </div>
</template>
<script>
import { getApplicablePlatforms, parsePromoLink } from '@/utils';
import { mapActions, mapGetters } from 'vuex';
import router from '@/router';
import { createAnalyticsEvent } from '@/helpers/ga-helper';

// Feature-linked buttons inserted by the admin editor, keyed by the data attribute that
// identifies them, mapped to the feature toggles for [digital, web-app].
//
// Visibility is decided here from the live feature toggles rather than from the
// `data-tappin-platforms` attribute the editor stamps on save. That attribute is a snapshot:
// it holds the raw PlatformPage enum (so a Lobby-only feature writes "1", which matched no
// surface and hid the button everywhere), it goes stale when platform settings change, and it
// was compared against a URL check that never recognises /live-stream as the web-app.
const FEATURE_BUTTONS = [
  ['data-tappin-agenda-session', 'stageTabAgenda', 'webAppNavigationAgenda'],
  ['data-tappin-session-id', 'stageTabAgenda', 'webAppNavigationAgenda'],
  ['data-tappin-sponsor-id', 'stageTabSponsors', 'webAppNavigationSponsors'],
  ['data-tappin-speaker-id', 'stageTabSpeakers', 'webAppNavigationSpeakers'],
  ['data-tappin-infopage-id', 'stageTabInfoPages', 'webAppNavigationInfoPages'],
  ['data-tappin-documents', 'stageTabDocuments', 'webAppNavigationDocuments'],
  [
    'data-tappin-participants',
    'stageTabParticipants',
    'webAppNavigationParticipants'
  ]
];

export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    // The host view's own idea of whether it is showing the web-app. LiveStream resolves this
    // from the participant's preference and the event mode, which the URL alone cannot tell.
    onWebapp: {
      type: Boolean,
      default: null
    }
  },
  created() {
    createAnalyticsEvent(
      this.$gtag,
      'View',
      'PromotedMessage',
      this.$props.item?.TimelineID
    );
  },
  computed: {
    ...mapGetters('CurrentEvent', ['features', 'participantVisibilityMode']),
    isImageClickable() {
      return ['PICTURE_ONLY_HIDDEN_BUTTON', 'CONTAINER'].includes(
        this.item.LinkMode
      );
    },
    isButtonClickable() {
      return ['BUTTON_LINK_ONLY', 'CONTAINER'].includes(this.item.LinkMode);
    },
    isWebapp() {
      if (typeof this.onWebapp === 'boolean') {
        return this.onWebapp;
      }

      return getApplicablePlatforms(this.$route).includes('webApp');
    },
    // Computed rather than parsed once on mount, so the message re-renders when the item,
    // the feature toggles or the surface change.
    parsedMessage() {
      if (!this.item.Message) {
        return '';
      }

      const parsedHtml = new DOMParser().parseFromString(
        this.item.Message,
        'text/html'
      );

      parsedHtml.querySelectorAll('a.button').forEach((button) => {
        const visible = this.isFeatureButtonVisible(button);
        if (visible === false) {
          button.remove();
        } else if (visible === true) {
          button.style.display = 'inline-block';
        }
      });

      parsedHtml.querySelectorAll('a[href]').forEach((link) => {
        const parts = link.href.split('/');
        const platform = parts[parts.length - 2];
        if (platform === '0' || platform === '2') {
          if (this.isWebapp === (platform === '2')) {
            link.style.display = 'inline-block';
          } else {
            link.remove();
          }
        }
      });

      return parsedHtml.documentElement.innerHTML;
    }
  },
  methods: {
    ...mapActions('Timeline', ['setPromotedPage']),
    onImageClick() {
      if (['PICTURE_ONLY_HIDDEN_BUTTON'].includes(this.item.LinkMode)) {
        return this.onPromoClick();
      }

      return null;
    },
    onButtonClick() {
      if (['BUTTON_LINK_ONLY', 'CONTAINER'].includes(this.item.LinkMode)) {
        return this.onPromoClick();
      }

      return null;
    },
    onPromoClick() {
      const promoted = parsePromoLink(this.item.Promolink);
      if (promoted.page === 'sponsor_detail') {
        return router.push({
          name: 'sponsor',
          params: { sponsorId: promoted.id },
          query: { fromStage: true }
        });
      }

      this.setPromotedPage(promoted);
    },
    // true = show, false = remove, null = not a feature button, leave untouched.
    isFeatureButtonVisible(button) {
      const feature = FEATURE_BUTTONS.find(([attr]) =>
        button.hasAttribute(attr)
      );

      if (!feature) {
        // Legacy fallback for anything outside the known feature set.
        const platforms = button.dataset.tappinPlatforms;
        if (!platforms) {
          return null;
        }
        return platforms === (this.isWebapp ? '2' : '0');
      }

      const [attr, digitalFlag, webappFlag] = feature;
      const features = this.features || {};

      // Fail open: only an explicit `false` hides the button, so it stays visible while the
      // event details are still loading instead of flashing out and back in.
      if (features[this.isWebapp ? webappFlag : digitalFlag] === false) {
        return false;
      }

      // "Not visible" participant mode hides the participant list everywhere, so a button
      // pointing at it would lead nowhere.
      if (
        attr === 'data-tappin-participants' &&
        this.participantVisibilityMode === 'hidden'
      ) {
        return false;
      }

      return true;
    }
  }
};
</script>
